import { getApp } from 'firebase/app';
import {
  getFirestore, doc, getDoc, setDoc, collection,
  serverTimestamp, runTransaction, Timestamp,
} from 'firebase/firestore';

const DB = () => { try { return getFirestore(getApp()); } catch (_) { return null; } };
const CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

export const INVITE_ROLE_OPTS = [
  { id: 'ops',          label: 'Operations Officer',   color: '#e11d48' },
  { id: 'mp',           label: 'Mission Planning',     color: '#6366f1' },
  { id: 'embedded',     label: 'Embedded Systems',     color: '#f59e0b' },
  { id: 'system',       label: 'Systems Engineering',  color: '#10b981' },
  { id: 'experimental', label: 'Experimental Results', color: '#06b6d4' },
  { id: 'finance',      label: 'Finance & Admin',      color: '#34d399' },
  { id: 'mentor',       label: 'Mentor',               color: '#a855f7' },
  { id: 'investor',     label: 'Investor',             color: '#f59e0b' },
  { id: 'guest',        label: 'Guest Reviewer',       color: '#06b6d4' },
  { id: 'pe',           label: 'Prompt Engineering',   color: '#8b5cf6' },
  { id: 'obc',          label: 'OBC Development',      color: '#00FFB2' },
  { id: 'admin',        label: 'Admin',                color: '#0e9cc0' },
];

export const GUEST_ROLES = new Set(['mentor', 'investor', 'guest']);

function randomCode() {
  return 'SMC-' + Array.from({ length: 4 }, () => CHARS[Math.floor(Math.random() * CHARS.length)]).join('');
}

export async function generateInviteCode({ role, maxUses = 1, expiresHours = 72, createdBy, createdByName }) {
  const db = DB();
  if (!db) throw new Error('Database unavailable');

  let code = randomCode();
  for (let i = 0; i < 3; i++) {
    const snap = await getDoc(doc(db, 'invite_codes', code));
    if (!snap.exists()) break;
    code = randomCode();
  }

  await setDoc(doc(db, 'invite_codes', code), {
    code, role, maxUses, usedCount: 0, active: true,
    expiresAt: Timestamp.fromDate(new Date(Date.now() + expiresHours * 3600000)),
    createdBy, createdByName,
    createdAt: serverTimestamp(),
  });

  return code;
}

export async function validateInviteCode(code) {
  const db = DB();
  if (!db) return { valid: false, error: 'Database unavailable' };

  try {
    const snap = await getDoc(doc(db, 'invite_codes', code.toUpperCase().trim()));
    if (!snap.exists()) return { valid: false, error: 'Invalid invite code' };

    const data = snap.data();
    if (!data.active) return { valid: false, error: 'This invite link has been revoked' };
    if (data.usedCount >= data.maxUses) return { valid: false, error: 'This invite has reached its usage limit' };
    if (data.expiresAt?.toDate() < new Date()) return { valid: false, error: 'This invite link has expired' };

    return { valid: true, role: data.role, code: code.toUpperCase().trim(), data };
  } catch (_) {
    return { valid: false, error: 'Could not validate invite code' };
  }
}

export async function redeemInviteCode({ code, uid, name, email, expiresAt }) {
  const db = DB();
  if (!db) throw new Error('Database unavailable');

  await runTransaction(db, async (tx) => {
    const ref  = doc(db, 'invite_codes', code);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('Invite code not found');

    const data = snap.data();
    if (!data.active) throw new Error('Invite code has been revoked');
    if (data.usedCount >= data.maxUses) throw new Error('Invite code usage limit reached');
    if (data.expiresAt?.toDate() < new Date()) throw new Error('Invite code has expired');

    tx.update(ref, { usedCount: data.usedCount + 1 });

    const userDoc = {
      uid, name, email,
      role: data.role, active: true,
      approvedAt: serverTimestamp(),
      approvedBy: 'invite_code',
      inviteCode: code,
    };
    if (expiresAt) userDoc.expiresAt = expiresAt;

    tx.set(doc(db, 'authorized_users', uid), userDoc);
  });
}

export async function revokeInviteCode(code) {
  const db = DB();
  if (!db) throw new Error('Database unavailable');
  await setDoc(doc(db, 'invite_codes', code), { active: false }, { merge: true });
}
